import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { add } from "date-fns";

import {
  Accordion,
  AccordionDetails,
  Grid as MuiGrid,
  Link,
  Typography as MuiTypography,
  Divider as MuiDivider,
  Breadcrumbs as MuiBreadcrumbs,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";

import useService from "../../../hooks/useService";
import { useApp } from "../../../AppProvider";
import { findRawRecords } from "../../../services/crudService";
import { dateFormatter, lineColors } from "../../../utils";

import Panel from "../../../components/panels/Panel";
import Map from "../../../components/map/Map";
import TimeseriesFilters from "../../../components/filters/TimeseriesFilters";
import SaveGraphButton from "../../../components/graphs/SaveGraphButton";
import OptionsPicker from "../../../components/pickers/OptionsPicker";
import Table from "../../../components/Table";
import TimeseriesLineChart from "../../../components/graphs/TimeseriesLineChart";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: calc(100% - 84px);
  width: 100%;
`;
const FiltersContainer = styled.div`
  height: 100%;
  width: 100%;
`;
const MapContainer = styled.div`
  height: 300px;
  width: 100%;
`;
const TimeseriesContainer = styled.div`
  height: 600px;
  overflow-y: auto;
  width: 100%;
`;
const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Grid = styled(MuiGrid)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TimeseriesTemperature = () => {
  const saveRef = useRef(null);

  //date filter defaults
  const defaultFilterValues = {
    previousDays: 7,
    startDate: add(new Date().getTime(), { days: -7 }),
    endDate: new Date(),
    checked: true,
  };
  const [filterValues, setFilterValues] = useState(defaultFilterValues);
  const changeFilterValues = (name, value) => {
    setFilterValues((prevState) => {
      let newFilterValues = { ...prevState };
      newFilterValues[name] = value;
      return newFilterValues;
    });
  };

  //columns and fields to render on table
  const tableColumns = [
    { title: "Location", field: "location_name", width: "100%" },
    {
      title: "Last Report",
      field: "collect_timestamp",
      render: (rowData) => {
        return dateFormatter(rowData.collect_timestamp, "MM/DD/YYYY, h:mm A");
      },
      customExport: (rowData) =>
        dateFormatter(rowData.collect_timestamp, "MM/DD/YYYY, h:mm A"),
    },
    { title: "Measured Temp f", field: "measured_temp_degf" },
    {
      title: "Rolling Two Hour Avg f",
      field: "temp_rolling_two_hour_avg_degf",
    },
    { title: "Daily Avg f", field: "temp_daily_avg_degf" },
    {
      title: "Rolling Seven Day Avg f",
      field: "temp_rolling_seven_day_avg_degf",
    },
    { title: "Measured Temp c", field: "measured_temp_degc" },
    // {
    //   title: "Rolling Two Hour Avg c",
    //   field: "temp_rolling_two_hour_avg_degc",
    // },
    // { title: "Daily Avg c", field: "temp_daily_avg_degc" },
    // {
    //   title: "Rolling Seven Day Avg c",
    //   field: "temp_rolling_seven_day_avg_degc",
    // },
    // {
    //   title: "Collect Gap",
    //   field: "collect_gap",
    //   render: (rowData) => {
    //     return dateFormatter(rowData.collect_gap, "MM/DD/YYYY, h:mm A");
    //   },
    // },
    // { title: "Measurement NDX", field: "measurement_ndx" },
    // { title: "Location NDX", field: "location_ndx" },
  ];

  const service = useService({ toast: false });
  const { currentUser } = useApp();

  //fetch all graph/table data
  const { data, isLoading, error } = useQuery(
    ["TimeseriesTemperatures", currentUser],
    async () => {
      try {
        return await service([findRawRecords, ["TimeseriesTemperatures"]]);
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // Derived from data — useMemo (was previously useEffect+setState, which
  // caused an extra render per data load).
  const locationsOptions = useMemo(() => {
    if (!data?.length) return [];
    return [...new Set(data.map((item) => item.location_name))];
  }, [data]);

  // Default to the first location once the picker options arrive.
  const [selectedLocation, setSelectedLocation] = useState("");
  useEffect(() => {
    if (locationsOptions.length > 0) {
      setSelectedLocation(locationsOptions[0] ?? "");
    }
  }, [locationsOptions]);

  // Pre-compute table date bounds as numeric ms once per filter change.
  const tableDateBounds = useMemo(() => {
    if (filterValues.previousDays === "") return null;
    if (filterValues.checked) {
      const now = Date.now();
      return {
        min: add(now, { days: -filterValues.previousDays }).getTime(),
        max: now,
      };
    }
    return {
      min: new Date(filterValues.startDate).getTime(),
      max: new Date(filterValues.endDate).getTime(),
    };
  }, [filterValues]);

  const filteredTableData = useMemo(() => {
    if (!data || !selectedLocation) return [];
    return data.filter((item) => {
      if (item.location_name !== selectedLocation) return false;
      if (!tableDateBounds) return true;
      const t = new Date(item.collect_timestamp).getTime();
      return t >= tableDateBounds.min && t <= tableDateBounds.max;
    });
  }, [data, selectedLocation, tableDateBounds]);

  const filteredMutatedGraphData = useMemo(() => {
    if (!data || !selectedLocation) {
      return { labels: [], datasets: [] };
    }
    const filteredData = data.filter(
      (item) => item.location_name === selectedLocation
    );

    const defaultStyle = {
      fill: false,
      tension: 0.5,
    };
    return {
      // Pre-parse labels to numeric ms so Chart.js's time scale skips
      // the moment string-parse on every update.
      labels: filteredData.map((item) =>
        new Date(item.collect_timestamp).getTime()
      ),
      datasets: [
        {
          label: "Daily Average",
          backgroundColor: lineColors.orange,
          borderColor: lineColors.orange,
          data: filteredData.map((item) => item.temp_daily_avg_degf),
          borderDash: [1, 2],
          borderWidth: 3,
          pointStyle: "circle",
          pointHoverRadius: 6,
          ...defaultStyle,
          spanGaps: true,
        },
        {
          label: "7 Day Average",
          borderColor: lineColors.green,
          backgroundColor: lineColors.green,
          data: filteredData.map(
            (item) => item.temp_rolling_seven_day_avg_degf
          ),
          borderWidth: 5,
          pointHoverBorderWidth: 7,
          pointHoverRadius: 10,
          radius: 8,
          pointStyle: "line",
          spanGaps: false,
        },
        {
          label: "2 Hour Average",
          backgroundColor: lineColors.blue,
          borderColor: lineColors.blue,
          data: filteredData.map((item) => item.temp_rolling_two_hour_avg_degf),
          pointRadius: 0,
          borderWidth: 4,
          pointStyle: "point",
          pointHoverRadius: 4,
          ...defaultStyle,
          spanGaps: true,
        },
        // {
        //   label: "Measured",
        //   borderColor: lineColors.blue,
        //   backgroundColor: lineColors.blue,
        //   data: filteredData.map((item) => item.measured_temp_degf),
        //   pointRadius: 0,
        //   borderWidth: 4,
        //   pointStyle: "line",
        //   pointHoverRadius: 0,
        //   ...defaultStyle,
        // },
      ],
    };
  }, [data, selectedLocation]);

  return (
    <>
      <Helmet title="Temperature" />
      <Typography variant="h3" gutterBottom display="inline">
        Temperature
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Temperature</Typography>
      </Breadcrumbs>

      <Divider my={6} />
      <Grid container spacing={6}>
        <Grid item xs={12} md={12} lg={7}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="map"
              id="map"
            >
              <Typography variant="h4" ml={2}>
                Map
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MapContainer>
                <Map />
              </MapContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid item xs={12} md={12} lg={5}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="date-filters"
              id="date-filters"
            >
              <Typography variant="h4" ml={2}>
                Date Filters
              </Typography>
            </AccordionSummary>
            <Panel>
              <AccordionDetails>
                <FiltersContainer>
                  <TimeseriesFilters
                    filterValues={filterValues}
                    changeFilterValues={changeFilterValues}
                  />
                </FiltersContainer>
              </AccordionDetails>
            </Panel>
          </Accordion>
        </Grid>
      </Grid>

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="time-series"
              id="time-series"
            >
              <Typography variant="h4" ml={2}>
                Graph
              </Typography>
            </AccordionSummary>
            <Panel>
              <AccordionDetails>
                <TimeseriesContainer>
                  <Grid container pb={6} mt={2}>
                    <Grid
                      item
                      style={{ flexGrow: 1, maxWidth: "calc(100% - 54px)" }}
                    >
                      <OptionsPicker
                        selectedOption={selectedLocation}
                        setSelectedOption={setSelectedLocation}
                        options={locationsOptions}
                        label="Locations"
                      />
                    </Grid>
                    <Grid item style={{ width: "53px" }}>
                      <SaveGraphButton
                        ref={saveRef}
                        title="Temperature Timeseries Graph"
                      />
                    </Grid>
                  </Grid>

                  <TableWrapper>
                    <TimeseriesLineChart
                      data={filteredMutatedGraphData}
                      error={error}
                      isLoading={isLoading}
                      filterValues={filterValues}
                      locationsOptions={locationsOptions}
                      yLLabel="Degrees F"
                      ref={saveRef}
                    />
                  </TableWrapper>
                </TimeseriesContainer>
              </AccordionDetails>
            </Panel>
          </Accordion>
        </Grid>
      </Grid>

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="table-content"
              id="table-header"
            >
              <Typography variant="h4" ml={2}>
                Table
              </Typography>
            </AccordionSummary>
            <Panel>
              <AccordionDetails>
                <TableWrapper>
                  <Table
                    isLoading={isLoading}
                    label="Temperature Timeseries Table"
                    columns={tableColumns}
                    data={filteredTableData}
                    height="600px"
                  />
                </TableWrapper>
              </AccordionDetails>
            </Panel>
          </Accordion>
        </Grid>
      </Grid>
    </>
  );
};

export default TimeseriesTemperature;
